import { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";

import { getPortalSectionsAPI } from "../../services/api";
import { useBreadcrumb } from "../../context/BreadcrumbContext";
import ErrorMessage from "../ui_elements/ErrorMessage";
import Section from "../ui_elements/transparency_portal/Section";

const findItemWithAncestors = (items, path, ancestors = []) => {
  for (const item of items) {
    if (item.path === path) return { item, ancestors };
    if (item.subItems) {
      const result = findItemWithAncestors(item.subItems, path, [
        ...ancestors,
        { title: item.title, path: item.path },
      ]);
      if (result) return result;
    }
  }
  return null;
};

const resolveSubItemUrl = (subItem, parentId) => {
  if (subItem.type === "EXTERNAL_FINAL_LINK") {
    return subItem.externalUrl;
  }
  if (subItem.type === "EXTERNAL_LINK") {
    return `/transparencia/${subItem.path}`;
  }
  return `/transparencia/documentos/${parentId}/${subItem.title}`;
};

const TransparencyPortalInternalLink = () => {
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { "*": path } = useParams();
  const { pathname } = useLocation();
  const { setExtraCrumbs } = useBreadcrumb();

  useEffect(() => {
    const fetchItem = async () => {
      setLoading(true);
      setExtraCrumbs(null);
      try {
        const response = await getPortalSectionsAPI();
        if (response && response.status === 200) {
          const { data } = response;
          let found = null;
          for (const section of data) {
            const result = findItemWithAncestors(section.items, path);
            if (result) {
              found = result.item;
              setExtraCrumbs({
                forPath: pathname,
                crumbs: [
                  { label: "Portal da Transparência", path: "/transparencia" },
                  ...result.ancestors.map((a) => ({
                    label: a.title,
                    path: `/transparencia/${a.path}`,
                  })),
                  { label: result.item.title },
                ],
              });
              break;
            }
          }
          setItem(found);
        }
      } catch (err) {
        console.error(err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchItem();
  }, [path]);

  if (loading) return <div className="py-8 text-center text-gray-500">Carregando dados...</div>;

  if (error || !item) return (
    <ErrorMessage
      title="Conteúdo não encontrado"
      description="O conteúdo solicitado não está disponível ou foi removido."
    />
  );

  const renderMain = () => {
    if (!item || !item?.subItems) {
      return (
        <ErrorMessage
          title="Essa seção está temporariamente em manutenção"
          description="No momento, as informações desta página do Portal da Transparência estão temporariamente indisponíveis. Nossa equipe já está trabalhando para restabelecer o acesso o mais breve possível."
        />
      )
    }
    return (
      <div className="p-8 space-y-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center uppercase">
          {item.title}
          <span className="flex-1 border-t border-gray-300 ml-4"></span>
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {item.subItems.map(subItem => (
            <Section
              key={subItem.id}
              name={subItem.title}
              url={resolveSubItemUrl(subItem, subItem.id)}
              icon={""}
            />
          ))}
        </div>
      </div>
    )
  }

  return (
    renderMain()
  );
};

export default TransparencyPortalInternalLink;
